import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { userApi } from '../api/user';
import { UserResponse } from '../types';
import { RefreshCcw, CheckCircle2, XCircle, Search, ArrowUpDown } from 'lucide-react';
import LoadingScreen from '../components/LoadingScreen';

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const AdRemovalManagementPage: React.FC = () => {
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'email' | 'createdAt'>('name');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userApi.getUsers();
      setUsers(res.data.result || []);
    } finally {
      setLoading(false);
    }
  }, []);

  const visibleUsers = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const filtered = users.filter((user) => {
      if (!normalized) return true;
      return (
        user.name.toLowerCase().includes(normalized) ||
        user.email.toLowerCase().includes(normalized)
      );
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === 'createdAt') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }

      const left = sortBy === 'email' ? a.email : a.name;
      const right = sortBy === 'email' ? b.email : b.name;
      return left.localeCompare(right, 'ko');
    });
  }, [query, sortBy, users]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleTicketUpdate = async (userId: number, hasAdRemovalTicket: boolean) => {
    setUpdatingUserId(userId);
    try {
      await userApi.updateAdRemovalTicket(userId, { purchased: hasAdRemovalTicket });
      await fetchUsers();
    } catch {
      alert('광고 제거 티켓 상태 변경 실패');
    } finally {
      setUpdatingUserId(null);
    }
  };

  if (loading) return <LoadingScreen message="광고 제거 상태를 불러오는 중" />;

  return (
    <div className="fade-in">
      <div className="page-header page-header-row">
        <div>
          <h1 className="page-title page-title-sm">광고제거 관리</h1>
          <p className="page-subtitle">유저별 광고 제거 티켓 보유 여부를 관리합니다.</p>
        </div>
        <button className="btn btn-outline" onClick={fetchUsers}>
          <RefreshCcw size={18} />
        </button>
      </div>

      <div className="dashboard-admin-toolbar" style={{ justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div className="dashboard-admin-search">
          <Search size={16} />
          <input
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="닉네임 또는 이메일 검색"
          />
        </div>
        <label className="dashboard-admin-sort">
          <ArrowUpDown size={16} />
          <select className="input" value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
            <option value="name">이름순</option>
            <option value="email">이메일순</option>
            <option value="createdAt">가입일 최신순</option>
          </select>
        </label>
      </div>

      <div className="dashboard-application-list">
        {visibleUsers.length > 0 ? (
          visibleUsers.map((user) => (
            <div key={user.id} className="dashboard-access-item">
              <div className="dashboard-access-head">
                <strong>{user.name}</strong>
                <span className={`badge ${user.hasAdRemovalTicket ? 'badge-active' : 'badge-inactive'}`}>
                  {user.hasAdRemovalTicket ? '보유' : '무료'}
                </span>
              </div>
              <div className="dashboard-access-meta">
                <span>{user.email}</span>
                <span>{formatDate(user.createdAt)}</span>
              </div>
              <div className="dashboard-admin-approval">
                <button
                  className="btn btn-primary"
                  onClick={() => handleTicketUpdate(user.id, true)}
                  disabled={updatingUserId === user.id}
                >
                  <CheckCircle2 size={16} /> 부여
                </button>
                <button
                  className="btn btn-outline"
                  onClick={() => handleTicketUpdate(user.id, false)}
                  disabled={updatingUserId === user.id}
                >
                  <XCircle size={16} /> 회수
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="dashboard-empty-note">유저 목록이 없습니다.</div>
        )}
      </div>
    </div>
  );
};

export default AdRemovalManagementPage;
